import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { AppTopRightMenu } from "@/components/app-layout/AppTopRightMenu";
import { DeleteDialog } from "@/components/dialogs/DeleteDialog";
import { GroupOrUnknownDisplayName } from "@/components/display/GroupDisplayName";
import { CreateGroupModal } from "@/components/group/CreateGroupModal";
import { GroupSelect } from "@/components/group/GroupSelect";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SalthubClientSelect } from "@/components/group/SalthubClientSelect";
import { useGroupData } from "@/data/use-groups-data";
import { useSettingData } from "@/data/use-settings-data";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faPlus,
  faTrash,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

export default function GlobalAdminGroupDetailsPage() {
  const { groupUuid } = useParams({ strict: false }) as {
    groupUuid?: string;
  };
  const { group, isLoading, deleteGroup, updateGroup } =
    useGroupData(groupUuid);
  const { group: parentGroup } = useGroupData(group?.parentGroupUuid);
  const { value: sharedGroupUuid } = useSettingData(
    groupUuid,
    "SHARED_WORKSPACE_UUID",
  );
  const [deleteOpened, setDeleteOpened] = useState(false);
  const [isCreatingChildGroup, setIsCreatingChildGroup] = useState(false);
  const navigate = useNavigate();

  const handleDelete = async () => {
    if (!groupUuid) return;

    try {
      await deleteGroup();
      toast.success("Group deleted successfully.");
      navigate({ to: "/admin/groups" });
    } catch {
      toast.error(
        "Failed to delete group. There might be some pending associated data that needs to be deleted first.",
      );
    }
  };

  const handleParentGroupChange = async (groupUuid: string) => {
    if (!group) return;

    try {
      await updateGroup({ parentGroupUuid: groupUuid });
      toast.success("Parent group updated successfully.");
    } catch {
      toast.error("Failed to update parent group.");
    }
  };

  // New users are added to every team group tagged with one of their
  // Salt People clients.
  const handleClientChange = async (salthubClientId: string | null) => {
    try {
      await updateGroup({ salthubClientId });
      toast.success(
        salthubClientId ? "Client updated successfully." : "Client cleared.",
      );
    } catch {
      toast.error("Failed to update client.");
    }
  };

  if (!group) return null;

  return (
    <>
      <AppTopRightMenu.ContentPortal>
        {!group.personal && (
          <Button onClick={() => setIsCreatingChildGroup(true)}>
            <FontAwesomeIcon icon={faPlus} /> Create Child Group
          </Button>
        )}
        <Link
          to={`/w/$groupSlug`}
          params={{ groupSlug: group.slug }}
          target="_blank"
        >
          <Button>
            <FontAwesomeIcon icon={faEye} /> Visit
          </Button>
        </Link>
      </AppTopRightMenu.ContentPortal>
      <Page>
        <PageHeader>
          <PageHeader.Title>Details</PageHeader.Title>
        </PageHeader>
        <PageBody innerClassName="gap-8">
          <dl className="grid grid-cols-[100px_1fr] items-center gap-y-2">
            <div className="contents">
              <dt className="text-sm font-semibold text-gray-500">Name</dt>
              {isLoading && <Skeleton className="h-4 w-1/3" />}
              {!isLoading && (
                <dd>
                  <GroupOrUnknownDisplayName group={group} />
                </dd>
              )}
            </div>
            <div className="contents">
              <dt className="text-sm font-semibold text-gray-500">Slug</dt>
              {isLoading && <Skeleton className="h-4 w-1/4" />}
              {!isLoading && <dd>{group.slug}</dd>}
            </div>
            <div className="contents">
              <dt className="text-sm font-semibold text-gray-500">UUID</dt>
              {isLoading && <Skeleton className="h-4 w-1/3" />}
              {!isLoading && <dd>{group.uuid}</dd>}
            </div>
            <div className="contents">
              <dt className="text-sm font-semibold text-gray-500">Parent</dt>
              <dd className="flex flex-row items-center gap-2">
                <GroupSelect
                  groupUuid={group.parentGroupUuid}
                  hideGroupUuid={group.uuid}
                  onChange={handleParentGroupChange}
                />
                {parentGroup && (
                  <Link
                    to={`/admin/groups/$groupUuid/details`}
                    params={{ groupUuid: parentGroup.uuid }}
                  >
                    <Button variant="link" className="px-0">
                      <FontAwesomeIcon icon={faEye} />
                    </Button>
                  </Link>
                )}
              </dd>
            </div>
            {!group.personal && (
              <div className="contents">
                <dt className="text-sm font-semibold text-gray-500">Client</dt>
                <dd>
                  <SalthubClientSelect
                    salthubClientId={group.salthubClientId}
                    onChange={handleClientChange}
                  />
                </dd>
              </div>
            )}
          </dl>

          {group.uuid !== sharedGroupUuid && (
            <div className="border-destructive text-destructive my-8 flex flex-col gap-4 rounded-md border-1 p-4">
              <div className="text-lg font-bold">Danger Zone</div>
              <div className="flex flex-row items-center justify-between">
                <div className="flex flex-col gap-2">
                  <div>
                    This will delete {group.name} permanently, and all its
                    associated data.
                  </div>
                </div>
                <Button
                  variant="destructive"
                  onClick={() => setDeleteOpened(true)}
                >
                  <FontAwesomeIcon icon={faTrash} />
                  Delete
                </Button>
              </div>
            </div>
          )}
        </PageBody>
      </Page>

      <DeleteDialog
        name={group.name}
        open={deleteOpened}
        onOpenChange={setDeleteOpened}
        onDeleteClick={handleDelete}
      />
      {!group.personal && (
        <CreateGroupModal
          open={isCreatingChildGroup}
          onOpenChange={setIsCreatingChildGroup}
          parentGroupUuid={group.uuid}
        />
      )}
    </>
  );
}
